import { useSyncExternalStore } from "react";

/**
 * Global impersonation state, in its own leaf module. Extracted from
 * ImperativeState to break a module cycle: trpc.ts needs `impersonationRef`
 * (to attach the acting-as header) while ImperativeState needs trpc's base-URL
 * helpers — with both in one file, whichever module loads first leaves the
 * other reading partially-evaluated exports, and bun's evaluation order for
 * that cycle shifts with unrelated import changes ("Export named 'trpc' not
 * found" test failures). This module imports nothing app-side, so it can't
 * re-enter a cycle.
 */

const GLOBAL_IMPERSONATION_KEY = "impersonateUserId";
// Per-round app modes (viewAsUserId/viewAsTrackMember/readOnly) and the
// recent-target lists — cleared together with the global key: they are all
// account-level impersonation state.
export const APP_MODE_STORAGE_PREFIX = "appMode-";
export const RECENT_IMPERSONATE_PREFIX = "impersonateRecent-";
export const GLOBAL_RECENT_KEY = "impersonateRecent";
// The account that owns ALL local impersonation state — including per-round
// modes, which can exist without the global key (View as Track Member).
const STATE_OWNER_KEY = "impersonationStateOwner";

export type GlobalImpersonation = {
  userId: string;
  displayName: string;
  // The impersonated user's own SFC-admin flag, captured when impersonation
  // starts. Mirrors the server, which resolves effectiveIsSfcAdmin from the
  // TARGET's flag — so viewing as an admin keeps admin surfaces visible.
  // Missing (older persisted entries) means false. Client-side courtesy only;
  // every admin route re-checks on the server.
  isSfcAdmin?: boolean;
  // The underlying (real) user who set this. Impersonation is account-level
  // state: a different account signing in on this device must not inherit it.
  setByUserId?: string;
};

export function getGlobalImpersonation(): GlobalImpersonation | null {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(GLOBAL_IMPERSONATION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed &&
      typeof parsed.userId === "string"
    ) {
      return {
        userId: parsed.userId,
        displayName:
          typeof parsed.displayName === "string"
            ? parsed.displayName
            : parsed.userId,
        isSfcAdmin: parsed.isSfcAdmin === true ? true : undefined,
        setByUserId:
          typeof parsed.setByUserId === "string"
            ? parsed.setByUserId
            : undefined,
      };
    }
    // Backwards compat: bare string from older format
    if (typeof parsed === "string") {
      return { userId: parsed, displayName: parsed };
    }
  } catch {
    // Bare string (not JSON-wrapped)
    if (typeof raw === "string" && raw) {
      return { userId: raw, displayName: raw };
    }
  }
  return null;
}

// Render-stable snapshot of the persisted entry, kept in lockstep by
// setGlobalImpersonation (every writer goes through it). A component that
// read storage directly at render never re-rendered on a sweep — the
// listener snapshot (the ref) can be unchanged null→null.
let cachedGlobalImpersonation: GlobalImpersonation | null | undefined;

export function setGlobalImpersonation(value: GlobalImpersonation | null) {
  cachedGlobalImpersonation = value;
  if (typeof localStorage === "undefined") return;
  try {
    if (value) {
      localStorage.setItem(GLOBAL_IMPERSONATION_KEY, JSON.stringify(value));
      if (value.setByUserId) stampImpersonationStateOwner(value.setByUserId);
    } else {
      localStorage.removeItem(GLOBAL_IMPERSONATION_KEY);
    }
  } catch (e) {
    console.warn("Failed to persist impersonation state:", e);
  }
}

/** Record which account owns the local impersonation state. Called by every
 * writer — including per-round modes, which exist without the global key. */
export function stampImpersonationStateOwner(userId: string) {
  try {
    localStorage.setItem(STATE_OWNER_KEY, userId);
  } catch {
    // Non-critical.
  }
}

/**
 * Read by the tRPC client (TRPCProvider / trpcVanilla) to attach the
 * `x-acting-as-user-id` header so the server can resolve effective identity.
 * Written by ClientWrapper.setMode().
 *
 * Deliberately NOT initialized from localStorage: persisted impersonation may
 * belong to a previous account (sessions end without the sign-out page), so
 * the header stays off until the live identity confirms the owner —
 * reconcileImpersonationOwner restores it on match. Round pages restore
 * their own per-round mode independently (works offline).
 *
 * Deliberate trade: the owner's own refresh briefly renders self-view on
 * non-round pages until the confirm lands. Restoring from the CACHED
 * identity instead would close that flash but reopen the cross-account
 * header leak exactly when it matters (a new sign-in still carries the
 * previous user's cache until the first getCurrentUser resolves).
 */
export const impersonationRef: { current: string | null } = {
  current: null,
};

/** Reactive read of the persisted global impersonation: re-renders on every
 * notified change, including a sweep that leaves the ref unchanged. */
export function useGlobalImpersonationValue(): GlobalImpersonation | null {
  return useSyncExternalStore(
    subscribeImpersonationListener,
    () => {
      if (cachedGlobalImpersonation === undefined) {
        cachedGlobalImpersonation = getGlobalImpersonation();
      }
      return cachedGlobalImpersonation;
    },
    () => null,
  );
}

/** Subscribers notified when impersonationRef.current changes via setMode(). */
const impersonationListeners = new Set<() => void>();

export function notifyImpersonationListeners() {
  for (const listener of impersonationListeners) {
    listener();
  }
}

/** Subscribe to impersonationRef change notifications. Returns unsubscribe. */
export function subscribeImpersonationListener(cb: () => void): () => void {
  impersonationListeners.add(cb);
  return () => impersonationListeners.delete(cb);
}

/**
 * React hook that subscribes to impersonationRef changes.
 * Unlike ImpersonateContext, this is NOT cleared on RoundWrapper unmount,
 * so it's safe to use as a dependency for the Round reload effect.
 */
export function useImpersonationRefValue(): string | null {
  return useSyncExternalStore(
    subscribeImpersonationListener,
    () => impersonationRef.current,
    () => null, // server snapshot — no impersonation during SSR
  );
}

/**
 * Stop impersonating entirely: remove the persisted global key, reset the
 * in-memory ref the tRPC clients read, and notify subscribers (banner,
 * header) so the UI updates immediately.
 */
export function clearGlobalImpersonation() {
  setGlobalImpersonation(null);
  impersonationRef.current = null;
  notifyImpersonationListeners();
}

/**
 * Remove every piece of persisted impersonation state on this device: the
 * global key, per-round app modes, and recent-target lists. Kept here (the
 * leaf module) so the owner reconcile below can sweep without importing
 * app modules.
 */
export function clearAllImpersonationLocalState() {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith(APP_MODE_STORAGE_PREFIX) ||
          key.startsWith(RECENT_IMPERSONATE_PREFIX) ||
          key === GLOBAL_RECENT_KEY)
      ) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) localStorage.removeItem(key);
    localStorage.removeItem(STATE_OWNER_KEY);
  } catch {
    // Non-critical; the global key below still clears.
  }
  setGlobalImpersonation(null);
  impersonationRef.current = null;
}

/**
 * Runs when the live signed-in user resolves. A persisted impersonation set
 * by this account is restored (the header stays off until then); one set by
 * a different account — or unstamped legacy state — is swept entirely,
 * including per-round modes, which would otherwise restore the previous
 * account's view-as state inside rounds.
 */
export function reconcileImpersonationOwner(realUserId: string) {
  if (typeof localStorage === "undefined") return;
  const owner = (() => {
    try {
      return localStorage.getItem(STATE_OWNER_KEY);
    } catch {
      return null;
    }
  })();
  const current = getGlobalImpersonation();

  if (owner === realUserId || current?.setByUserId === realUserId) {
    if (current != null && impersonationRef.current !== current.userId) {
      impersonationRef.current = current.userId;
      notifyImpersonationListeners();
    }
    return;
  }

  // A different owner, an unstamped legacy global entry, or — with no stamp
  // at all — any per-round mode carrying view-as state (View as Track Member
  // writes only appMode-<roundId>): all swept.
  const hasImpersonationState =
    owner != null ||
    current != null ||
    (() => {
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (!key?.startsWith(APP_MODE_STORAGE_PREFIX)) continue;
          const raw = localStorage.getItem(key);
          if (!raw) continue;
          try {
            const mode = JSON.parse(raw);
            if (mode?.viewAsUserId != null || mode?.viewAsTrackMember != null) {
              return true;
            }
          } catch {
            // Unparseable mode: treat as suspect state.
            return true;
          }
        }
      } catch {
        return false;
      }
      return false;
    })();
  if (!hasImpersonationState) return;
  clearAllImpersonationLocalState();
  notifyImpersonationListeners();
}
